import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce, omit, uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import useFilterCategoryLabels from '~hooks/useFilterCategoryLabels';
import { InfiniteQuery } from '~queries/constants';
import useAccountsInfiniteQuery from '~queries/useAccountsInfiniteQuery';
import useAiReviewStatusInfiniteQuery from '~queries/useAiReviewStatusInfiniteQuery';
import useBillingApprovalStatusInfiniteQuery from '~queries/useBillingApprovalStatusInfiniteQuery';
import useDepartmentsInfiniteQuery from '~queries/useDepartmentsInfiniteQuery';
import useDispatchNumbersInfiniteQuery from '~queries/useDispatchNumbersInfiniteQuery';
import useDriversInfiniteQuery from '~queries/useDriversInfiniteQuery';
import useEquipmentOwnersInfiniteQuery from '~queries/useEquipmentOwnersInfiniteQuery';
import useEquipmentTypesInfiniteQuery from '~queries/useEquipmentTypesInfiniteQuery';
import useForemenInfiniteQuery from '~queries/useForemenInfiniteQuery';
import useJobStatusInfiniteQuery from '~queries/useJobStatusInfiniteQuery';
import useMaterialsInfiniteQuery from '~queries/useMaterialsInfiniteQuery';
import useOrderStatusInfiniteQuery from '~queries/useOrderStatusInfiniteQuery';
import usePrevailingWageInfiniteQuery from '~queries/usePrevailingWageInfiniteQuery';
import useProjectExternalIdsInfiniteQuery from '~queries/useProjectExternalIdsInfiniteQuery';
import useProjectsInfiniteQuery from '~queries/useProjectsInfiniteQuery';
import useRequestersInfiniteQuery from '~queries/useRequestersInfiniteQuery';
import useSiteTypesInfiniteQuery from '~queries/useSiteTypesInfiniteQuery';
import useTicketPotentialDuplicateInfiniteQuery from '~queries/useTicketPotentialDuplicateInfiniteQuery';
import useTicketTypesInfiniteQuery from '~queries/useTicketTypesInfiniteQuery';
import useWaypointInfiniteQuery from '~queries/useWaypointInfiniteQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { EXCLUDED_JOB_STATES } from '../../constants';
import FilesQueryContext from '../../FilesQueryContext';

const QUERY_PREFIX = 'FilesPage_OrderFiles';

export enum FilesAvailableFilter {
  AI_REVIEW_STATUSES = 'ai_review_statuses',
  CUSTOMERS = 'customers',
  DEPARTMENTS = 'departments',
  DISPATCH_NUMBERS = 'dispatch_numbers',
  DRIVERS = 'drivers',
  DROPOFF_SITES = 'dropoff_sites',
  DUPLICATE_TICKETS = 'duplicate_tickets',
  EQUIPMENT_OWNERS = 'equipment_owners',
  EQUIPMENT_TYPES = 'equipment_types',
  FOREMEN = 'foremen',
  JOB_APPROVAL_STATUS = 'job_approval_status',
  JOB_STATES = 'job_states',
  LOAD_APPROVAL_STATUS = 'load_approval_status',
  MATERIALS = 'materials',
  ORDER_STATES = 'order_states',
  PICKUP_SITES = 'pickup_sites',
  PREVAILING_WAGE = 'prevailing_wage',
  PROJECTS = 'projects',
  PROJECT_EXTERNAL_IDS = 'project_external_ids',
  REQUESTERS = 'requesters',
  SITE_TYPES = 'site_types',
  TICKET_TYPES = 'ticket_types',
  VENDORS = 'vendors',
}

type SearchQueryState = {
  input: string;
  debounced: string;
};

interface OrdersFiltersProps {
  hiddenFilters?: FilesAvailableFilter[];
}

const OrdersFilters = observer(({ hiddenFilters = [] }: OrdersFiltersProps) => {
  const { allEntitiesLabel, categoryLabel } = useFilterCategoryLabels();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const aiReviewLoadsEnabled = userStore.userCompany.aiReviewLoads;
  const defaultDigitalTicketSettingEnabled = userStore.companySettings.isEnabled(
    'show_default_digital_ticket_view',
  );

  const { filters: appliedFilters, applyFilters } = useContext(FilesQueryContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilesAvailableFilter>();
  const [searchQuery, setSearchQuery] = useState<SearchQueryState>({
    input: '',
    debounced: '',
  });

  const aiReviewStatusQuery = useAiReviewStatusInfiniteQuery();
  const billingApprovalStatusQuery = useBillingApprovalStatusInfiniteQuery();
  const customersQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_customers`,
    companyId,
    AccountType.CUSTOMER,
  );
  const siteTypesQuery = useSiteTypesInfiniteQuery();
  const vendorsQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_vendors`,
    companyId,
    AccountType.VENDOR,
  );
  const dispatchNumbersQuery = useDispatchNumbersInfiniteQuery(
    `${QUERY_PREFIX}_dispatchNumbers`,
    companyId,
  );
  const driversQuery = useDriversInfiniteQuery(`${QUERY_PREFIX}_drivers`, companyId);
  const equipmentOwnersQuery = useEquipmentOwnersInfiniteQuery(
    `${QUERY_PREFIX}_equipmentOwners`,
    companyId,
    { id: companyId, name: userStore.currentCompanies?.[0]?.legalName ?? '' },
  );
  const equipmentTypesQuery = useEquipmentTypesInfiniteQuery(
    `${QUERY_PREFIX}_equipmentTypes`,
    companyId,
  );
  const foremenQuery = useForemenInfiniteQuery(`${QUERY_PREFIX}_foremen`, companyId);
  const projectsQuery = useProjectsInfiniteQuery(`${QUERY_PREFIX}_projects`, companyId);
  const projectExternalIdsQuery = useProjectExternalIdsInfiniteQuery(
    `${QUERY_PREFIX}_projectExternalIds`,
    companyId,
  );
  const departmentsQuery = useDepartmentsInfiniteQuery(
    `${QUERY_PREFIX}_departments`,
    companyId,
  );
  const jobStatusQuery = useJobStatusInfiniteQuery(EXCLUDED_JOB_STATES);
  const materialsQuery = useMaterialsInfiniteQuery(
    `${QUERY_PREFIX}_materials`,
    companyId,
  );
  const ticketPotentialDuplicateQuery = useTicketPotentialDuplicateInfiniteQuery();
  const ticketTypesQuery = useTicketTypesInfiniteQuery();
  const orderStatusQuery = useOrderStatusInfiniteQuery();
  const prevailingWageQuery = usePrevailingWageInfiniteQuery();
  const requestersQuery = useRequestersInfiniteQuery(
    `${QUERY_PREFIX}_requesters`,
    companyId,
  );
  const pickupSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_pickupSites`,
    companyId,
    'pickup',
    {
      customerAccountIds: appliedFilters.customerAccountIds,
      departmentIds: appliedFilters.departmentIds,
      dispatchNumberIds: appliedFilters.dispatchNumberIds,
      driverIds: appliedFilters.driverIds,
      dropoffSiteIds: appliedFilters.dropoffSiteIds,
      endDate: appliedFilters.endDate?.endOf('day').toISOString(),
      jobStates: appliedFilters.jobStates,
      orderStates: appliedFilters.orderStates,
      projectIds: appliedFilters.projectIds,
      projectExternalIds: appliedFilters.projectExternalIds,
      requesterIds: appliedFilters.requesterIds,
      siteTypes: appliedFilters.siteTypes,
      startDate: appliedFilters.startDate?.startOf('day').toISOString(),
      vendorAccountIds: appliedFilters.vendorAccountIds,
    },
  );
  const dropffSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_dropffSites`,
    companyId,
    'drop_off',
    {
      customerAccountIds: appliedFilters.customerAccountIds,
      departmentIds: appliedFilters.departmentIds,
      dispatchNumberIds: appliedFilters.dispatchNumberIds,
      driverIds: appliedFilters.driverIds,
      endDate: appliedFilters.endDate?.endOf('day').toISOString(),
      jobStates: appliedFilters.jobStates,
      orderStates: appliedFilters.orderStates,
      pickupSiteIds: appliedFilters.pickupSiteIds,
      projectIds: appliedFilters.projectIds,
      projectExternalIds: appliedFilters.projectExternalIds,
      requesterIds: appliedFilters.requesterIds,
      siteTypes: appliedFilters.siteTypes,
      startDate: appliedFilters.startDate?.startOf('day').toISOString(),
      vendorAccountIds: appliedFilters.vendorAccountIds,
    },
  );

  const filesQueryFilterKeyByFilesAvailableFilter: {
    [key in FilesAvailableFilter]: keyof typeof appliedFilters;
  } = {
    [FilesAvailableFilter.AI_REVIEW_STATUSES]: 'aiReviewStatuses',
    [FilesAvailableFilter.CUSTOMERS]: 'customerAccountIds',
    [FilesAvailableFilter.DEPARTMENTS]: 'departmentIds',
    [FilesAvailableFilter.DISPATCH_NUMBERS]: 'dispatchNumberIds',
    [FilesAvailableFilter.DRIVERS]: 'driverIds',
    [FilesAvailableFilter.DROPOFF_SITES]: 'dropoffSiteIds',
    [FilesAvailableFilter.DUPLICATE_TICKETS]: 'ticketPotentialDuplicate',
    [FilesAvailableFilter.EQUIPMENT_OWNERS]: 'equipmentOwnerCompanyIds',
    [FilesAvailableFilter.EQUIPMENT_TYPES]: 'equipmentTypeIds',
    [FilesAvailableFilter.FOREMEN]: 'foremanIds',
    [FilesAvailableFilter.JOB_APPROVAL_STATUS]: 'jobApprovalStatus',
    [FilesAvailableFilter.JOB_STATES]: 'jobStates',
    [FilesAvailableFilter.LOAD_APPROVAL_STATUS]: 'loadApprovalStatus',
    [FilesAvailableFilter.MATERIALS]: 'materialIds',
    [FilesAvailableFilter.ORDER_STATES]: 'orderStates',
    [FilesAvailableFilter.PICKUP_SITES]: 'pickupSiteIds',
    [FilesAvailableFilter.PREVAILING_WAGE]: 'prevailingWage',
    [FilesAvailableFilter.PROJECTS]: 'projectIds',
    [FilesAvailableFilter.PROJECT_EXTERNAL_IDS]: 'projectExternalIds',
    [FilesAvailableFilter.REQUESTERS]: 'requesterIds',
    [FilesAvailableFilter.SITE_TYPES]: 'siteTypes',
    [FilesAvailableFilter.TICKET_TYPES]: 'ticketTypes',
    [FilesAvailableFilter.VENDORS]: 'vendorAccountIds',
  };

  const queriesByFilesAvailableFilter: Record<FilesAvailableFilter, InfiniteQuery> = {
    [FilesAvailableFilter.AI_REVIEW_STATUSES]: aiReviewStatusQuery,
    [FilesAvailableFilter.CUSTOMERS]: customersQuery,
    [FilesAvailableFilter.DEPARTMENTS]: departmentsQuery,
    [FilesAvailableFilter.DISPATCH_NUMBERS]: dispatchNumbersQuery,
    [FilesAvailableFilter.DRIVERS]: driversQuery,
    [FilesAvailableFilter.DROPOFF_SITES]: dropffSitesQuery,
    [FilesAvailableFilter.DUPLICATE_TICKETS]: ticketPotentialDuplicateQuery,
    [FilesAvailableFilter.EQUIPMENT_OWNERS]: equipmentOwnersQuery,
    [FilesAvailableFilter.EQUIPMENT_TYPES]: equipmentTypesQuery,
    [FilesAvailableFilter.FOREMEN]: foremenQuery,
    [FilesAvailableFilter.JOB_APPROVAL_STATUS]: billingApprovalStatusQuery,
    [FilesAvailableFilter.JOB_STATES]: jobStatusQuery,
    [FilesAvailableFilter.LOAD_APPROVAL_STATUS]: billingApprovalStatusQuery,
    [FilesAvailableFilter.MATERIALS]: materialsQuery,
    [FilesAvailableFilter.ORDER_STATES]: orderStatusQuery,
    [FilesAvailableFilter.PICKUP_SITES]: pickupSitesQuery,
    [FilesAvailableFilter.PREVAILING_WAGE]: prevailingWageQuery,
    [FilesAvailableFilter.PROJECTS]: projectsQuery,
    [FilesAvailableFilter.PROJECT_EXTERNAL_IDS]: projectExternalIdsQuery,
    [FilesAvailableFilter.REQUESTERS]: requestersQuery,
    [FilesAvailableFilter.SITE_TYPES]: siteTypesQuery,
    [FilesAvailableFilter.TICKET_TYPES]: ticketTypesQuery,
    [FilesAvailableFilter.VENDORS]: vendorsQuery,
  };

  const isSearching = Boolean(appliedFilters.searchQuery?.length);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    return queriesByFilesAvailableFilter[selectedFilter];
  }, [selectedFilter, queriesByFilesAvailableFilter]);

  const filtersForChips = omit(appliedFilters, [
    'endDate',
    'searchQuery',
    'startDate',
    'payablesStates',
    'receivablesStates',
    'jobApprovalStatus',
    'loadApprovalStatus',
  ]);
  const hasFiltersForChips = Object.values(filtersForChips).some((filter) => {
    return Array.isArray(filter) ? filter.length > 0 : Boolean(filter);
  });
  const [filterChipsCollectedData, setFilterChipsCollectedData] = useState<
    Partial<Record<keyof typeof appliedFilters, FilterMenuOption[]>>
  >({});

  useEffect(() => {
    if (selectedFilter && activeQuery?.data) {
      setFilterChipsCollectedData((state) => {
        const key = filesQueryFilterKeyByFilesAvailableFilter[selectedFilter];
        const updatedEntry = uniqBy(
          [...(state[key] ?? []), ...activeQuery.data],
          (option) => option.value,
        );

        const newCollectedData = {
          ...state,
          [key]: updatedEntry.length > 0 ? updatedEntry : undefined,
        };

        return newCollectedData;
      });
    }
  }, [selectedFilter, activeQuery?.data]);
  useEffect(() => {
    setFilterChipsCollectedData((state) => ({
      ...state,
      aiReviewStatuses: aiReviewStatusQuery.data,
      jobApprovalStatus: billingApprovalStatusQuery.data,
      jobStates: jobStatusQuery.data,
      loadApprovalStatus: billingApprovalStatusQuery.data,
      prevailingWage: prevailingWageQuery.data,
      ticketPotentialDuplicate: ticketPotentialDuplicateQuery.data,
      ticketTypes: ticketTypesQuery.data,
    }));
  }, [
    aiReviewStatusQuery.data,
    billingApprovalStatusQuery.data,
    jobStatusQuery.data,
    prevailingWageQuery.data,
    ticketPotentialDuplicateQuery.data,
    ticketTypesQuery.data,
  ]);

  const searchValueChangeCallback = useMemo(() => {
    if (activeQuery?.isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [activeQuery?.isDummyQuery]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = filesQueryFilterKeyByFilesAvailableFilter[selectedFilter];
    const options = appliedFilters[key] ?? [];

    return options as string[];
  }, [selectedFilter, appliedFilters]);

  const handleSelectedOptionsChange = useCallback(
    (options: string[]) => {
      if (selectedFilter) {
        const newSelectedOptions = options.length > 0 ? options : undefined;
        const key = filesQueryFilterKeyByFilesAvailableFilter[selectedFilter];

        applyFilters({ [key]: newSelectedOptions });
      }
    },
    [selectedOptions, selectedFilter],
  );

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => [filter, undefined]),
    );

    applyFilters(filtersObject);
  }, []);

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      Object.values(queriesByFilesAvailableFilter).forEach((query) => {
        query.clear();
      });
    }
  }, [selectedFilter]);

  if (isSearching) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsMenuOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={(theme) => ({
              [`&.${buttonClasses.root}`]: {
                bgcolor: 'background.paper',
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersForChips
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            })}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            loadingReason={activeQuery?.fetchReason}
            onFetchMore={activeQuery?.fetchMore}
            onSearchValueChange={searchValueChangeCallback}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            options={activeQuery?.data ?? []}
            searchValue={searchQuery.input}
            selectAllOptionLabel={
              (activeQuery?.data?.length ?? 0) <= 1
                ? undefined
                : allEntitiesLabel(selectedFilter)
            }
            selectedOptions={selectedOptions}
          />
        )}

        {!selectedFilter &&
          Object.values(FilesAvailableFilter)
            .filter(
              (filter) =>
                !hiddenFilters.includes(filter) &&
                (aiReviewLoadsEnabled ||
                  filter !== FilesAvailableFilter.AI_REVIEW_STATUSES) &&
                (defaultDigitalTicketSettingEnabled ||
                  filter !== FilesAvailableFilter.TICKET_TYPES),
            )
            .map((filter) => (
              <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                  >
                    {categoryLabel(filter)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {Boolean(
                      appliedFilters[filesQueryFilterKeyByFilesAvailableFilter[filter]],
                    ) && (
                      <Check
                        sx={(theme) => ({
                          color: theme.brandV2.colors.treadOrange,
                          fontSize: '16px',
                        })}
                      />
                    )}

                    <ChevronRight sx={{ fontSize: '16px' }} />
                  </Box>
                </Box>
              </MenuItem>
            ))}
      </Menu>

      <AppliedFiltersChips
        appliedFilters={filtersForChips}
        collectedData={filterChipsCollectedData}
        onRemoveAllFilters={handleRemoveAllFilters}
        onRemoveFilter={handleRemoveAllFilters}
      />
    </Box>
  );
});

export default OrdersFilters;
